import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TablePagination from '@mui/material/TablePagination';
import Box from '@mui/material/Box';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APIContext } from '../../../Contexts/APIContext';
import { SessionContext } from '../../../Contexts/SessionContext';
import { SurveyViewModel } from '../../../Contexts/SessionState';
import SortableTableHead, { HeadCell, Order } from '../../../Utilities/SortableTableHead';
import SurveyTableViewModel from '../../ViewModels/SurveyTableViewModel';
import DeselectionReasonDialog from './DeselectionReasonDialog';
import SurveySelectionRow from './SurveySelectionRow';
import { Stack } from '@mui/material';
import { useTripRateContext } from '../../TripRateContext';
import { getFilteredSurveys } from '../../../TripRateNew/TriprateFilters';
import { useSearchParams } from "react-router-dom";
import { navigateToSite } from "../../../Site/SiteNavigation";
import TripRateSitesGraphDialog from '../../../Charts/TripRate/TripRateSitesGraphDialog';
import { TripRateSitesGraphDirection, TripRateSitesGraphView } from '../../../Charts/TripRate/TripRateSitesGraphTypes';
import ModeSelectionViewModel from '../../ViewModels/ModeSelectionViewModel';
import { getValidActiveSurveyRefs, surveyRefsMatch } from '../../TripRateSurveyRefs';
import { surveySelectionColumnSx } from './SurveySelectionTableStyles';

function getComparator<T, Key extends keyof T>(
    order: Order,
    orderBy: Key,
): (a: T, b: T) => number {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    const aValue = a[orderBy];
    const bValue = b[orderBy];

    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;

    // Date
    if (aValue instanceof Date && bValue instanceof Date) {
        return bValue.getTime() - aValue.getTime();
    }

    // boolean
    if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        return (aValue === bValue) ? 0 : aValue ? -1 : 1;
    }

    // number
    if (typeof aValue === "number" && typeof bValue === "number") {
        return bValue - aValue;
    }

    //  string
    if (typeof aValue === "string" && typeof bValue === "string") {
        return bValue.localeCompare(aValue);
    }

    // Fallback option
    return String(bValue).localeCompare(String(aValue));
}

const headCells: HeadCell<SurveyTableViewModel>[] = [
    { id: 'surveyId', label: 'Reference', show: true, sx: surveySelectionColumnSx.reference },
    { id: 'date', label: 'Date', show: true, sx: surveySelectionColumnSx.date },
    { id: 'dayOfWeek', label: 'Day of Week', show: true, sx: surveySelectionColumnSx.dayOfWeek },
    { id: 'type', label: 'Survey Type', show: true, sx: surveySelectionColumnSx.surveyType },
    { id: 'description', label: 'Location Type', show: true, sx: surveySelectionColumnSx.locationType },
    { id: 'town', label: 'Town/City', show: true, sx: surveySelectionColumnSx.town },
];

const resurveyText = (surveyId: string) => `Removed: Site re-surveyed by ${surveyId}`;
const latestSurveyText = (surveyId: string) => `Removed: Most Recent ${surveyId}`;
const busiestSurveyText = (surveyId: string) => `Removed: Busiest Survey ${surveyId}`;

type SurveyMode = 'latest' | 'busiest' | 'all';

interface IProps {
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SurveySelectionTab({ setLoading }: IProps) {
    const { surveys } = useTripRateContext()
    const navigate = useNavigate();
    const { post, postCSV } = useContext(APIContext);
    const { sessionState, updateSessionState } = useContext(SessionContext);

    const [searchParams] = useSearchParams();
    const stopRefresh = searchParams.get("fromSession") === "true" || searchParams.get("fromResults") === "true";

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const loc = params.get('loc') || '';

    const [order, setOrder] = useState<Order>('asc');
    const [rows, setRows] = useState<SurveyTableViewModel[]>([]);
    const [orderBy, setOrderBy] = useState<keyof SurveyTableViewModel>('surveyId');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const [deselectionRefs, setDeselectionRefs] = useState<string[] | null>(null);

    const [multiDeselect, setMultiDeselect] = useState<boolean>(false);

    const toggleMultiDeselect = () => {
        setMultiDeselect(!multiDeselect);
    }

    const [allSurveysDeselected, setAllSurveysDeselected] = useState<boolean>(false);
    const [selectionMode, setSelectionMode] = useState<SurveyMode>('latest');
    const [graphOpen, setGraphOpen] = useState(false);
    const [graphView, setGraphView] = useState<TripRateSitesGraphView>("selection");
    const [graphMode, setGraphMode] = useState<number | null>(null);
    const [graphDirection, setGraphDirection] = useState<TripRateSitesGraphDirection>("Total");
    const [graphModes, setGraphModes] = useState<ModeSelectionViewModel[]>([]);

    const filteredSurveys = useMemo(
        () =>
            getFilteredSurveys(
                surveys
            ),
        [surveys, sessionState.primaryFilters, sessionState.secondaryFilters, sessionState.residentialUnitTypes]
    );

    useEffect(() => {
        if (filteredSurveys.length === 0) {
            setGraphModes([]);
            return;
        }

        const activeSurveyRefs = getValidActiveSurveyRefs(
            sessionState.selectedSitesAndSurveys,
            filteredSurveys.map(survey => survey.surveyRef),
        );
        const activeSurveys = sessionState.selectedSitesAndSurveys
            .filter(survey => survey.siteActive && survey.surveyActive)
            .filter(survey => activeSurveyRefs.some(reference => surveyRefsMatch(reference, survey.surveyRef)));
        const multimodal = sessionState.landusePage.isMultiModal;
        const atc = !multimodal
            && sessionState.primaryFilters.surveyTypes.includes(2)
            && sessionState.atcCount > 0;
        const servicing = sessionState.primaryFilters.serviceVehicles.includes("Y");
        const startDate = activeSurveys
            .map(survey => survey.surveyRef.split("|")[1] ?? survey.date)
            .map(value => {
                const parts = value?.split("-") ?? [];
                if (parts.length === 3 && parts[2].length === 4) {
                    return `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
                }

                const parsed = new Date(value);
                return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
            })
            .filter((date): date is string => date !== null)
            .sort()[0];
        const startDateQuery = startDate ? `&startdate=${startDate}` : "";

        post(
            "TripRateCalculation",
            `GetModes?atc=${atc}&servicing=${servicing}&multimodal=${multimodal}${startDateQuery}`,
            activeSurveyRefs,
            setGraphModes
        );
    }, [filteredSurveys, sessionState.selectedSitesAndSurveys]);

    useEffect(() => {

        if (filteredSurveys.length === 0) return;

        getData();
    }, [filteredSurveys]);

    useEffect(() => {
        if (stopRefresh) return;

        const data = sessionState.selectedSitesAndSurveys;

        if (data.length === 0) {
            return;
        }

        const updated = data.map(row => {
            const vm = new SurveyViewModel();
            vm.surveyRef = row.surveyRef;
            vm.siteRef = row.siteRef;
            vm.busiestSurveyId = row.busiestSurveyId;
            vm.resurveyRef = row.resurveyRef;
            vm.latestSurveyId = row.latestSurveyId;
            vm.date = vm.date;

            vm.siteActive = row.siteActive;
            vm.surveyActive = row.siteActive && ((selectionMode === 'latest' && row.surveyRef === row.latestSurveyId) || (selectionMode === 'busiest' && row.surveyRef === row.busiestSurveyId) || selectionMode === 'all');

            vm.reasonForSiteRemoval = row.reasonForSiteRemoval;

            if (!vm.surveyActive) {
                if (!vm.siteActive) {
                    vm.reasonForSurveyRemoval = row.reasonForSurveyRemoval
                } else if (row.resurveyRef && !vm.siteActive) {
                    vm.reasonForSurveyRemoval = resurveyText(row.resurveyRef);
                } else if (selectionMode === 'latest') {
                    vm.reasonForSurveyRemoval = latestSurveyText(row.latestSurveyId)
                } else if (selectionMode === 'busiest') {
                    vm.reasonForSurveyRemoval = busiestSurveyText(row.busiestSurveyId);
                }
            }

            return vm;
        });

        updateSessionState({ selectedSitesAndSurveys: updated });
    }, [selectionMode]);

    const setSurveyId = (selectedRow: SurveyTableViewModel) => {
        navigateToSite(navigate, location, {
            siteRef: selectedRow.siteId,
            type: 'summaryFiltering',
            fromTab: 'surveySummary',
            returnLabel: 'Back to Survey Selection'
        });
    }

    const openGraphDialog = () => {
        setGraphOpen(true);
        setGraphView("selection");
        setGraphMode(null);
        setGraphDirection("Total");
    };

    const closeGraphDialog = () => {
        setGraphOpen(false);
    };

    const getData = () => {
        setLoading(true);
        const vm = { surveys: filteredSurveys, isMultiModal: sessionState.landusePage.isMultiModal };

        post('TripRateFilter', 'GetSelectedSurveys', vm, (data: SurveyTableViewModel[]) => {
            const rowsForSession = stopRefresh && sessionState.selectedSitesAndSurveys.length !== 0
                ? data.filter(row => sessionState.selectedSitesAndSurveys.some(saved =>
                    surveyRefsMatch(saved.surveyRef, row.surveyId)))
                : data;

            setRows(rowsForSession);

            if (data.length === 0 || loc === "sitePage" || sessionState.selectedSitesAndSurveys.length !== 0) {
                setLoading(false);
                return;
            }

            const updated = data.map(row => {
                const vm = new SurveyViewModel();
                vm.surveyRef = row.surveyId;
                vm.siteRef = row.siteId;
                vm.siteActive =
                    !data.some(s => s.siteId === row.resurveyId) ||
                    (() => {
                        const parts = row.resurveyId.split("-");
                        return parts[1] !== sessionState.tripRateMain && parts[2] !== sessionState.tripRateSub;
                    })();

                vm.surveyActive = vm.siteActive && (selectionMode === 'latest' ? row.surveyId === row.latestSurveyId : row.surveyId === row.busiestSurveyId);
                vm.reasonForSurveyRemoval = (row.resurveyId && !vm.surveyActive)
                    ? resurveyText(row.resurveyId)
                    : (row.latestSurveyId && !vm.surveyActive && selectionMode === 'latest')
                        ? latestSurveyText(row.latestSurveyId)
                        : (!vm.surveyActive && selectionMode === 'busiest')
                            ? busiestSurveyText(row.busiestSurveyId)
                            : "";

                vm.reasonForSiteRemoval = (row.resurveyId && !vm.siteActive)
                    ? resurveyText(row.resurveyId)
                    : "";
                vm.resurveyRef = row.resurveyId;
                vm.latestSurveyId = row.latestSurveyId;
                vm.busiestSurveyId = row.busiestSurveyId;
                vm.date = row.date;

                return vm;
            });
            updateSessionState({ selectedSitesAndSurveys: updated }); // not here
            setLoading(false);
        });
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof SurveyTableViewModel) => {
        const isAsc = sessionState.siteListSort === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const visibleRows = useMemo(
        () =>
            [...rows] //get list of non active, if they match a row element don't display it
                .filter(x => sessionState.selectedSitesAndSurveys.filter(f => f.siteActive === false).map(m => m.siteRef).includes(x.siteId) === false)
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [order, orderBy, page, rowsPerPage, rows, sessionState.selectedSitesAndSurveys],
    );

    const closeDeselectionDialog = () => {
        setDeselectionRefs(null);
    };

    const handleSingleDeselection = (ref: string) => {
        let refs = deselectionRefs ?? [];
        const i = refs.indexOf(ref);
        if (i > 0) {
            refs.splice(i, 1);
        } else {
            refs.push(ref);
        }
        setDeselectionRefs(refs);
    };

    const toggleAllSurveys = () => {
        const selected: SurveyViewModel[] = sessionState?.selectedSitesAndSurveys ?? [];
        if (!selected.length) return;

        if (!allSurveysDeselected) {
            const allRefs = Array.from(
                new Set(
                    selected
                        .map(s => s?.surveyRef?.split('|')[0]?.trim())
                        .filter((r): r is string => Boolean(r))
                )
            );
            if (!allRefs.length) return;
            setAllSurveysDeselected(true);
            setDeselectionRefs(allRefs);
        } else {
            const updated = selected.map(item => {
                const vm = new SurveyViewModel();
                Object.assign(vm, item);
                vm.surveyActive = true;
                vm.reasonForSurveyRemoval = '';
                return vm;
            });

            updateSessionState({ selectedSitesAndSurveys: updated });
            setAllSurveysDeselected(false);
        }
    };

    const exportSurveyList = () => {

        const exportData = rows.map((row, index) => {
            const siteSurvey = sessionState.selectedSitesAndSurveys[index];
            const reason =
                siteSurvey?.reasonForSurveyRemoval ||
                siteSurvey?.reasonForSiteRemoval ||
                "";

            return {
                ...row,
                reasonForDeselection: reason
            };
        });
        postCSV("CsvGenerator", `GetSurveyListCSV`, "Trip Rate Surveys Export", exportData);
    }
   
    return (
        <Box>
            <Grid container sx={{ pt: 2 }}>
                <Grid size={12}>
                    <Stack spacing={2} direction={"row"} justifyContent={"right"}>
                        <Button fullWidth={false} onClick={openGraphDialog}>Trip Rate Line Graph</Button>
                        <Button fullWidth={false} onClick={exportSurveyList}>Export</Button>
                    </Stack>
                </Grid>
                <Grid size={12}>
                    <FormControl sx={{ mr: 2 }}>
                        <RadioGroup
                            value={selectionMode}
                            onChange={e => setSelectionMode(e.target.value as SurveyMode)}
                            row
                        >
                            <FormControlLabel
                                value="latest"
                                control={<Radio size="small" />}
                                label="Most Recent Survey Only"
                            />
                            <FormControlLabel
                                value="busiest"
                                control={<Radio size="small" />}
                                label="Busiest Survey"
                            />
                            <FormControlLabel
                                value="all"
                                control={<Radio size="small" />}
                                label="All Surveys"
                            />
                        </RadioGroup>
                    </FormControl>
                    <TableContainer>
                        <Table
                            size="small"
                            sx={{
                                width: '100%',
                                tableLayout: 'auto',
                                '& .MuiTableCell-root': {
                                    fontSize: '0.8125rem',
                                    px: 1,
                                },
                                '& .MuiTableCell-head': { fontWeight: 700 },
                                '& .MuiTableSortLabel-root': { fontWeight: 700 },
                            }}
                        >
                            <SortableTableHead
                                firstCells={<TableCell align="center" sx={surveySelectionColumnSx.include}>Include</TableCell>}
                                headCells={headCells}
                                onRequestSort={handleRequestSort}
                                order={order}
                                orderBy={orderBy}
                                lastCells={<TableCell sx={surveySelectionColumnSx.reason}>Reason for Removal</TableCell>}
                            />
                            <TableBody>
                                {visibleRows.map((row) => (
                                    <SurveySelectionRow
                                        key={row.surveyId}
                                        row={row}
                                        setDeselectionOpen={handleSingleDeselection}
                                        mode={selectionMode}
                                        setSelectedRowId={setSurveyId}
                                        multiDeselect={multiDeselect}
                                    />
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 50]}
                        component="div"
                        count={rows.filter(x => sessionState.selectedSitesAndSurveys.filter(f => f.siteActive === false).map(m => m.siteRef).includes(x.siteId) === false).length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </Grid>
                <Grid size={6} />
                <Grid size={3}>
                    <Button onClick={toggleMultiDeselect} disabled={!sessionState.selectedSitesAndSurveys.some(s => s.surveyPendingDeselect && s.surveyActive) && multiDeselect} fullWidth variant={multiDeselect ? "contained" : "outlined"}>
                        {multiDeselect ? "Confirm Deselection" : "Deselect Multiple"}
                    </Button>
                </Grid>
                <Grid size={3}>
                    <Button onClick={toggleAllSurveys}>
                        {allSurveysDeselected ? "Select All Surveys" : "Deselect All Surveys"}
                    </Button>
                </Grid>

                {(deselectionRefs && !multiDeselect) &&
                    <DeselectionReasonDialog
                        open={deselectionRefs !== null}
                        onClose={closeDeselectionDialog}
                        references={deselectionRefs}
                        type={'survey'}
                        multiDeselection={multiDeselect}
                    />
                }

                <TripRateSitesGraphDialog
                    open={graphOpen}
                    onClose={closeGraphDialog}
                    origin="surveySelection"
                    returnLabel="Back to Survey Selection"
                    returnTo={`${location.pathname}${location.search}`}
                    selectedMode={graphMode}
                    direction={graphDirection}
                    view={graphView}
                    allowModeSelection={true}
                    availableModes={graphModes}
                    onSelectedModeChange={setGraphMode}
                    onDirectionChange={setGraphDirection}
                    onViewChange={setGraphView}
                />

            </Grid>
        </Box >
    );
}
